import { Hit } from 'instantsearch.js';
import { UmbracoAlgoliaCategories } from '~/lib';
import { algoliaClient } from './client';
import { InstantSearchTopQueries } from './types';
import { ALGOLIA_INDEX_QUERY_SUGGESTIONS } from './variables';
import { serverLog } from '~/lib/better-stack';

const showOnlyOnline = (filter: string) => (filter ? `${filter} AND online:true` : 'online:true');

const generateAlgoliaConfigPropsInternal = ({
    filter,
    filterType,
    productIds,
    campaign,
}: {
    filter: string;
    filterType?: keyof typeof UmbracoAlgoliaCategories;
    productIds?: string | string[];
    campaign?: string | string[];
}) => {
    if (!filterType) return {};

    if (typeof productIds === 'string') {
        const pidFilter = 'id:' + productIds.split(',').join(' OR id:');
        return { filters: showOnlyOnline(pidFilter) };
    }

    if (typeof campaign === 'string') {
        return {
            filters: showOnlyOnline(
                `(cust_activepromo.fulllist.promotionid:"${campaign}" OR cust_activepromo.promotionid:"${campaign}")`,
            ),
        };
    }

    if (filterType === 'Custom') {
        return { filters: showOnlyOnline(filter) };
    }

    const query = filter.split(',').join(' AND ');

    if (filterType === 'Context Rule Name') {
        const isOnSearchPage = filter === 'search';
        return {
            // Makes sure content spots are not shown on searched pages
            filters: showOnlyOnline(isOnSearchPage ? 'NOT type:promotional_content' : ''),

            ruleContexts: filter.split(','),
        };
    }
    const filters = `${UmbracoAlgoliaCategories[filterType]}: ${JSON.stringify(query)}`;

    return {
        filters: showOnlyOnline(filters),
        ruleContexts: [],
    };
};

export const generateAlgoliaConfiguration = ({
    filter,
    filterType,
    parentFilter,
    parentFilterType,
    productIds,
    campaign,
}: {
    filter: string;
    filterType?: keyof typeof UmbracoAlgoliaCategories;

    parentFilter?: string;
    parentFilterType?: keyof typeof UmbracoAlgoliaCategories;
    productIds?: string | string[];
    campaign?: string | string[];
}) => {
    const algoliaProps = generateAlgoliaConfigPropsInternal({
        filter: filter,
        filterType: filterType,
        productIds,
        campaign,
    });

    if (parentFilter && parentFilterType) {
        const parentAlgoliaProps = generateAlgoliaConfigPropsInternal({
            filter: parentFilter,
            filterType: parentFilterType,
        });
        algoliaProps.filters += ` AND ${parentAlgoliaProps.filters}`;
        if (parentAlgoliaProps.ruleContexts) {
            algoliaProps.ruleContexts = algoliaProps.ruleContexts?.concat(
                parentAlgoliaProps.ruleContexts,
            );
        }
    }
    return algoliaProps;
};

// Gift card listings are excluded from the stock filter.
export const hasGiftCardRuleContext = (ruleContexts?: unknown[]) =>
    !!ruleContexts?.some((rule) => typeof rule === 'string' && rule.toLowerCase() === 'gavekort');

export const getPopularSearchSuggestions = async (): Promise<InstantSearchTopQueries | null> => {
    try {
        const request = await algoliaClient.search([
            {
                indexName: ALGOLIA_INDEX_QUERY_SUGGESTIONS,
                params: {
                    hitsPerPage: 12,
                    clickAnalytics: true,
                },
            },
        ]);

        const result = request.results.at(0);

        if (!result) return null;

        const hits = result.hits.map((hit) => {
            // We'll shave down as much unneeded data to save memory.
            const { _highlightResult: _, ...dataToSave } = hit;

            return dataToSave;
        }) as unknown as Hit[];

        return {
            hits,
            total: result.nbHits,
        };
    } catch (error) {
        console.error(error);

        serverLog({
            event: 'Server:Content:InstantSearchTopSearches:Error',
            data: { error },
        });

        return { hits: [], total: 0 };
    }
};
